import { Component } from '@verndale/core';

const SCROLL_TOLERANCE = 4;
const BODY_LOCK_CLASS = 'has-consent-modal';

// State lives in the DOM (attributes) or in module-level maps rather than in
// class fields: `useDefineForClassFields` runs field initializers *after*
// `super()` has already called `setupDefaults()`, which would wipe them.
const initializedRoots = new WeakSet<HTMLElement>();
// Identity rather than a data attribute: an attribute travels with cloneNode, so
// a re-injected copy of a live dialog would look bound and never get listeners.
const boundDialogs = new WeakSet<HTMLDialogElement>();

let mutationObserver: MutationObserver | null = null;
let pendingScan = 0;

/**
 * Expand/collapse consent modules plus the gated Privacy Notice dialog.
 */
class FormConsent extends Component {
  setupDefaults() {
    this.relocateDialogs();
    this.el.classList.add('is-enhanced');
    this.collapseAll();
  }

  addListeners() {
    this.el.addEventListener('click', this.handleRootClick.bind(this));
    this.getDialogs().forEach(dialog => this.bindDialog(dialog));
  }

  /**
   * A <dialog> left inside a Sitecore <form> submits its own controls with the
   * form and loses top-layer focus handling, so each one is hoisted to <body>.
   */
  relocateDialogs() {
    this.el.querySelectorAll<HTMLDialogElement>('dialog.consent-modal').forEach(dialog => {
      if (dialog.parentElement === document.body) return;

      // A server re-render ships a fresh dialog carrying the same id while the
      // previously hoisted one is still in <body>; drop the stale copy first so
      // getElementById cannot resolve to it.
      const stale = document.querySelector<HTMLDialogElement>(
        `body > dialog.consent-modal#${CSS.escape(dialog.id)}`
      );
      if (stale && stale !== dialog) {
        stale.remove();
      }

      document.body.appendChild(dialog);
    });
  }

  getDialogs(): HTMLDialogElement[] {
    const ids = Array.from(
      this.el.querySelectorAll<HTMLElement>('[data-consent-modal-trigger]')
    ).map(trigger => trigger.dataset.consentModalTrigger || '');

    Array.from(this.el.querySelectorAll<HTMLElement>('[data-consent-requires-agreement]')).forEach(
      checkbox => ids.push(checkbox.dataset.consentRequiresAgreement || '')
    );

    return Array.from(new Set(ids.filter(Boolean)))
      .map(id => document.getElementById(id))
      .filter((el): el is HTMLDialogElement => el instanceof HTMLDialogElement);
  }

  /**
   * Panels are authored open so the consent wording survives a missing bundle;
   * collapsing them is the module's first job once it does run.
   */
  collapseAll() {
    this.el.querySelectorAll<HTMLElement>('.form-consent__trigger').forEach(trigger => {
      const panel = this.getPanel(trigger);
      if (panel) this.setExpanded(trigger, panel, false);
    });
  }

  getPanel(trigger: HTMLElement): HTMLElement | null {
    const id = trigger.getAttribute('aria-controls');
    return id ? this.el.querySelector<HTMLElement>(`#${CSS.escape(id)}`) : null;
  }

  setExpanded(trigger: HTMLElement, panel: HTMLElement, expanded: boolean) {
    panel.hidden = !expanded;
    trigger.setAttribute('aria-expanded', String(expanded));

    // The plus/minus pair are SVG elements, which have no `hidden` IDL property.
    const plus = trigger.querySelector('.accordion__button__icon--plus');
    const minus = trigger.querySelector('.accordion__button__icon--minus');
    plus?.toggleAttribute('hidden', expanded);
    minus?.toggleAttribute('hidden', !expanded);
  }

  handleRootClick(event: Event) {
    const target = event.target as HTMLElement;

    const trigger = target.closest<HTMLElement>('.form-consent__trigger');
    if (trigger && this.el.contains(trigger)) {
      event.preventDefault();
      const panel = this.getPanel(trigger);
      if (panel) this.setExpanded(trigger, panel, panel.hidden);
      return;
    }

    const modalTrigger = target.closest<HTMLElement>('[data-consent-modal-trigger]');
    if (modalTrigger && this.el.contains(modalTrigger)) {
      event.preventDefault();
      this.openDialog(modalTrigger.dataset.consentModalTrigger, null);
      return;
    }

    const gatedCheckbox = target.closest<HTMLInputElement>('[data-consent-requires-agreement]');
    if (gatedCheckbox && this.el.contains(gatedCheckbox)) {
      this.handleGatedCheckbox(event, gatedCheckbox);
    }
  }

  handleGatedCheckbox(event: Event, checkbox: HTMLInputElement) {
    if (!checkbox.checked || checkbox.dataset.consentAgreed === 'true') return;

    // Consent has to be given inside the dialog, after the policy was scrolled.
    event.preventDefault();
    this.openDialog(checkbox.dataset.consentRequiresAgreement, checkbox);
  }

  openDialog(dialogId: string | undefined, checkbox: HTMLInputElement | null) {
    if (!dialogId) return;

    const dialog = document.getElementById(dialogId);
    if (!(dialog instanceof HTMLDialogElement)) return;

    const body = dialog.querySelector<HTMLElement>('.consent-modal__body');
    dialog.dataset.consentTarget = checkbox?.id || '';

    if (body) {
      body.scrollTop = 0;
    }

    document.body.classList.add(BODY_LOCK_CLASS);
    dialog.showModal();
    body?.focus();

    // Only measurable once the dialog is in the top layer — a closed dialog has
    // a zero-sized scroll box, which reads as "already scrolled to the end".
    this.updateScrollGate(dialog);
  }

  bindDialog(dialog: HTMLDialogElement) {
    if (boundDialogs.has(dialog)) return;
    boundDialogs.add(dialog);

    const body = dialog.querySelector<HTMLElement>('.consent-modal__body');
    const agree = dialog.querySelector<HTMLButtonElement>('.consent-modal__agree');
    const hint = dialog.querySelector<HTMLElement>('[data-consent-modal-hint]');

    // The authored hint is the "keep scrolling" copy; stash it so the gate can
    // clear and restore it without hard-coding a string in the module.
    if (hint && hint.dataset.hintText === undefined) {
      hint.dataset.hintText = hint.textContent?.trim() || '';
    }

    body?.addEventListener('scroll', () => this.updateScrollGate(dialog), { passive: true });

    // Catches the "content is shorter than the viewport" case and any reflow
    // that turns a scrollable body into a non-scrollable one.
    if (body && 'ResizeObserver' in window) {
      new ResizeObserver(() => this.updateScrollGate(dialog)).observe(body);
    }

    agree?.addEventListener('click', () => this.acceptDialog(dialog));

    dialog
      .querySelector<HTMLButtonElement>('.modal__close')
      ?.addEventListener('click', () => this.closeDialog(dialog));
    dialog
      .querySelector<HTMLButtonElement>('.consent-modal__decline')
      ?.addEventListener('click', () => this.closeDialog(dialog));

    // Light dismiss: the dialog element itself is the full-viewport overlay.
    dialog.addEventListener('click', event => {
      if (event.target === dialog) {
        this.closeDialog(dialog);
      }
    });

    // Escape dismisses the dialog natively. Some embedded Chromium builds never
    // deliver `close`, so the scroll lock is also released straight off the key.
    dialog.addEventListener('keydown', event => {
      if (event.key === 'Escape') {
        this.releaseBodyLock();
      }
    });
    dialog.addEventListener('close', () => this.releaseBodyLock());
  }

  closeDialog(dialog: HTMLDialogElement) {
    dialog.close();
    this.releaseBodyLock();
  }

  releaseBodyLock() {
    document.body.classList.remove(BODY_LOCK_CLASS);
  }

  updateScrollGate(dialog: HTMLDialogElement) {
    const body = dialog.querySelector<HTMLElement>('.consent-modal__body');
    const agree = dialog.querySelector<HTMLButtonElement>('.consent-modal__agree');
    const hint = dialog.querySelector<HTMLElement>('[data-consent-modal-hint]');
    if (!body || !agree) return;

    const reachedEnd =
      body.scrollTop + body.clientHeight >= body.scrollHeight - SCROLL_TOLERANCE;

    agree.disabled = !reachedEnd;

    if (hint) {
      hint.textContent = reachedEnd ? '' : hint.dataset.hintText || '';
    }
  }

  acceptDialog(dialog: HTMLDialogElement) {
    const targetId = dialog.dataset.consentTarget;
    const checkbox = targetId
      ? this.el.querySelector<HTMLInputElement>(`#${CSS.escape(targetId)}`)
      : this.el.querySelector<HTMLInputElement>(
          `[data-consent-requires-agreement="${dialog.id}"]`
        );

    if (checkbox) {
      checkbox.dataset.consentAgreed = 'true';
      checkbox.checked = true;
      // Sitecore's unobtrusive validation and FXB field conditions both listen
      // for `change`, so the programmatic check has to announce itself.
      checkbox.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.closeDialog(dialog);
  }

  static autoInit() {
    document.querySelectorAll<HTMLElement>('.form-consent').forEach(root => {
      if (initializedRoots.has(root)) return;
      initializedRoots.add(root);
      new FormConsent(root);
    });

    FormConsent.watchSitecoreForms();
  }

  /**
   * Sitecore FXB re-renders form sections over AJAX between pages/conditions,
   * which drops any markup the initial pass had already enhanced.
   */
  static watchSitecoreForms() {
    if (mutationObserver) return;

    const scopes = Array.from(document.querySelectorAll<HTMLElement>('form[data-sc-fxb]'))
      .map(form => form.parentElement)
      .filter((el): el is HTMLElement => !!el);

    if (!scopes.length) return;

    mutationObserver = new MutationObserver(() => {
      if (pendingScan) return;
      pendingScan = window.requestAnimationFrame(() => {
        pendingScan = 0;
        FormConsent.autoInit();
      });
    });

    scopes.forEach(scope => {
      mutationObserver?.observe(scope, { childList: true, subtree: true });
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', FormConsent.autoInit);
} else {
  FormConsent.autoInit();
}

// The scoped observer only survives while the watched form container does. Server
// flows that swap the whole container (FXB multi-page forms) call this instead.
(window as Window & { HollisterFormConsent?: { refresh: () => void } }).HollisterFormConsent = {
  refresh: () => FormConsent.autoInit()
};

export default FormConsent;
